import * as React from "react";

import dayjs from "dayjs";
import { QRCodeSVG } from "qrcode.react";

import { ReceiptResponse } from "api/models";
import { getPriceFormat } from "shared/functions";

import Logo from "assets/images/logo-auth.svg";

import "./index.scss";

interface Props {
  receipt: ReceiptResponse | null;
}

const ReceiptComponent: React.FC<Props> = (props) => {
  const { receipt } = props;

  if (receipt) {
    const {
      companyName,
      companyAddress,
      localNumber,
      receiptProducts,
      productsTotalAmount = 0,
      totalAmount = 0,
      providedCashAmount = 0,
      changeAmount = 0,
      roundingAmount = 0,
      discountAmount = 0,
      discountPercentage = 0,
      receiptTimeUtc,
      pointOfSaleName,
      isCashPayment,
      qrReceiptUrl,
      isFiscalReceipt,
      companyTin,
      cashierName,
      fiscalNumber,
      cashRegisterFiscalNumber,
      reportName,
      isOffline,
      receiptCardPayment,
      offlineCheckSum,
      isPublicNotes,
      notes,
      nonCashPaymentType,
    } = receipt;

    const {
      acquirerIdentifier,
      terminalIdentifier,
      maskedPan,
      paymentSystem,
      approvalCode,
      rrn,
    } = receiptCardPayment || {};

    const receiptItem = (title: string, data: string | null | number = "", price?: boolean) => {
      return (
        <div className="receiptSummary__text-wrapper">
          <div>
            <p className="receiptSummary__item">{title}</p>
          </div>
          <div className={`receiptSummary__value-wrapper ${price && "receiptSummary__price-wrapper"}`}>
            <p className={`receiptSummary__item ${price && "receiptSummary__price"}`}>
              {data}
            </p>
          </div>
        </div>
      );
    };

    return (
      <div>
        <section className="receiptSummary">
          <div className="receiptSummary__wrapper">
            <p className="receiptSummary__text">
              {pointOfSaleName}
              <br />
              {companyName}
            </p>
            <p className="receiptSummary__text">{companyAddress}</p>
            {isFiscalReceipt && (
              <p className="receiptSummary__text">
                ІД: {companyTin} <br />
                Касир: {cashierName}
              </p>
            )}
          </div>
          <div className="receiptSummary__wrapper">
            {receiptProducts &&
              receiptProducts.map(
                ({
                  name,
                  price = 1,
                  quantity = 1,
                  productProperties,
                  unit,
                  barcode,
                }) => (
                  <div key={name} className="receiptSummary__text-wrapper">
                    <p className="receiptSummary__text receiptSummary__item">
                      {unit.isDiscrete
                        ? Number(quantity).toFixed(2)
                        : Math.round(quantity)}{" "}
                      {unit.symbol}. x {getPriceFormat(price)} = <br />
                      {!!barcode && barcode}
                      {!!barcode && <br />}
                      {!!productProperties && productProperties}
                      {!!productProperties && <br />}
                      {name}
                    </p>
                    <p className="receiptSummary__text">
                      <span className="receiptSummary__price">
                        {getPriceFormat(price * quantity)}
                      </span>
                    </p>
                  </div>
                )
              )}
          </div>
          {receiptCardPayment && (
            <div className="receiptSummary__wrapper">
              {receiptItem("ІДЕНТ. ЕКВАЙРА  ", acquirerIdentifier)}
              {receiptItem("ТЕРМІНАЛ", terminalIdentifier)}
              {receiptItem("ЕПЗ", maskedPan)}
              {receiptItem("ПЛАТІЖНА СИСТЕМА ", paymentSystem)}
              {receiptItem("КОД АВТ.", approvalCode)}
              {receiptItem("RRN", rrn)}
            </div>
          )}
          <div className="receiptSummary__wrapper">
            {isCashPayment && receiptItem("Форма оплати:", "Готівка, ГРН")}
            {!isCashPayment && (
              <>
                {receiptItem("Форма оплати:", "Безготівкова, ГРН")}
                {receiptItem("Вид операції:", "Оплата")}
                {receiptItem("Засіб оплати:", nonCashPaymentType?.name || "Електронний платіжний засіб")}
              </>
            )}
            {receiptItem("Сума:", getPriceFormat(productsTotalAmount), true)}
            {!!discountAmount && receiptItem(
              `Знижка${discountPercentage ? ` ${discountPercentage}%` : ""}, грн`,
              getPriceFormat(discountAmount),
              true
            )}
            {isCashPayment && !!roundingAmount && receiptItem("Заокруглення:", getPriceFormat(roundingAmount), true)}
            <div className="receiptSummary__text-wrapper">
              <p className="receiptSummary-text receiptSummary__text receiptSummary__top">
                ДО СПЛАТИ:
              </p>
              <p className="receiptSummary-text receiptSummary__text receiptSummary__top">
                {getPriceFormat(totalAmount)}
              </p>
            </div>
            {isCashPayment && (
              <div className="receiptSummary__text-wrapper">
                <p className="receiptSummary__text">
                  Отримано:<br />
                  Решта:
                </p>
                <div className="receiptSummary__price-wrapper">
                  <p className="receiptSummary__text">
                    {getPriceFormat(providedCashAmount)}<br />
                    {getPriceFormat(changeAmount)}
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="receiptSummary__wrapper">
            <div className="receiptSummary__text-wrapper">
              <div>
                <p className="receiptSummary__text">Без ПДВ</p>
              </div>
            </div>
          </div>
          <div className="receiptSummary__wrapper">
            <div className="receiptSummary__text-wrapper">
              {fiscalNumber && (
                <p className="receiptSummary__text">ЧЕК № {fiscalNumber}</p>
              )}
              <p className="receiptSummary__text">ВН: {localNumber}</p>
            </div>
            <div className="receiptSummary__text-wrapper">
              <p className="receiptSummary__text">
                Дата:{" "}
                {dayjs
                  .utc(receiptTimeUtc)
                  .local()
                  .format("DD.MM.YYYY, HH:mm:ss")}
              </p>
            </div>
          </div>
          <div className="receiptSummary__wrapper">
            <div className="receiptSummary__qrcode-wrapper">
              <QRCodeSVG value={qrReceiptUrl || ""} size={160} level="Q" />
            </div>
            {isFiscalReceipt && (
              <div className="receiptSummary__text-wrapper">
                <div>
                  <p className="receiptSummary__text">
                    Режим роботи: {isOffline ? "офлайн" : "онлайн"}
                  </p>
                  {isOffline && (
                    <p className="receiptSummary__text">
                      Контрольне число: {offlineCheckSum}
                    </p>
                  )}
                  {isFiscalReceipt && (
                    <p className="receiptSummary__text">
                      ФН ПРРО: {cashRegisterFiscalNumber}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {notes && isPublicNotes ? (
            <div className="receiptSummary__wrapper">
              <div className="receiptSummary__text-wrapper">
                <div>
                  <p className="receiptSummary__text">Коментар:</p>
                </div>
              </div>
              <div className="receiptSummary__text-wrapper">
                <div>
                  <p className="receiptSummary__text">{notes}</p>
                </div>
              </div>
            </div>
          ) : null}
          <p className="receiptSummary__text">{reportName}</p>

          <div className="receiptSummary__img-wrapper">
            <img width={70} src={Logo} />
          </div>
        </section>
      </div>
    );
  }

  return null;
};

export default ReceiptComponent;
